import type { MeResponse } from '@qlk/shared';
import { X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useDangXuat } from '../lib/auth.js';
import { DaiCheDoDuLieu, hienDaiCheDo } from '../ui/DaiCheDoDuLieu.js';
import { kieuKyCuaDuongDan, KyChungProvider } from './kyChung.js';
import { Sidebar } from './Sidebar.js';
import { ThanhTren } from './ThanhTren.js';

export interface AppShellProps {
  me: MeResponse;
  children: ReactNode;
}

/**
 * Khung ứng dụng: menu trái tối cố định, thanh trên trắng, nội dung ở giữa.
 *
 * Trên điện thoại menu thành **ngăn kéo** mở bằng nút ☰ của thanh trên — thanh
 * trên luôn có mặt ở mọi khổ, nên breadcrumb, tìm kiếm và ô chọn kỳ không biến
 * mất khi hẹp (bản trước ẩn hẳn chúng dưới 1024px).
 */
export function AppShell({ me, children }: AppShellProps) {
  const [moNganKeo, setMoNganKeo] = useState(false);
  /*
   * Thu gọn thanh bên còn icon.
   *
   * Giữ trong **state React**, không `localStorage`: app này chưa lưu gì ở
   * trình duyệt — thứ cần giữ lại thì nằm trên URL (xem `kyChung`) — và một
   * lựa chọn hiển thị không đáng để mở ra ngoại lệ đầu tiên.
   */
  const [menuGon, setMenuGon] = useState(false);
  const dangXuat = useDangXuat();
  const dieuHuong = useNavigate();
  const viTri = useLocation();

  // Đổi trang thì đóng ngăn kéo.
  useEffect(() => {
    setMoNganKeo(false);
  }, [viTri.pathname]);

  // Máy chủ thật không hiện dải, nên chiều cao dải bằng 0 — thanh trên và
  // sidebar dính sát đỉnh thay vì chừa một khoảng trống 32px.
  const hienDai = hienDaiCheDo(me.youtubeMode, me.appEnv);

  const xuLyDangXuat = () => {
    dangXuat.mutate(undefined, {
      onSettled: () => {
        void dieuHuong('/dang-nhap', { replace: true });
      },
    });
  };

  return (
    /* `--cao-dai`: chiều cao dải nhãn, một nguồn cho cả dải, thanh trên và sidebar. */
    <KyChungProvider kieu={kieuKyCuaDuongDan(viTri.pathname)}>
      <div
        /* `khung-app` (styles.css): `min-height: 100dvh` có dự phòng `100vh`.
           Chỉ **tối thiểu** — tài liệu dài hơn thì cuộn theo cửa sổ, và khung
           app luôn cao bằng tài liệu nên không hở dải nền nào ở đáy. */
        className={`khung-app bg-nen ${hienDai ? '[--cao-dai:32px]' : '[--cao-dai:0px]'} ${
          /* Bề rộng thanh bên là **một biến**, để thanh bên và mọi phép đo
             khác đọc cùng một con số. */
          menuGon
            ? '[--rong-thanh-ben:var(--rong-menu-gon)]'
            : '[--rong-thanh-ben:var(--rong-menu)]'
        }`}
      >
        {/* Đang ở DB nào — hiện trên cùng mọi trang, trước cả thanh điều hướng.
            Trên máy chủ thật thì dải `live` không hiện (xem DaiCheDoDuLieu). */}
        <DaiCheDoDuLieu
          youtubeMode={me.youtubeMode}
          databaseName={me.databaseName}
          appEnv={me.appEnv}
        />

        {moNganKeo && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <button
              type="button"
              aria-label="Đóng menu"
              onClick={() => {
                setMoNganKeo(false);
              }}
              className="absolute inset-0 bg-chu/50"
            />
            <div className="absolute inset-y-0 left-0 w-(--rong-menu) max-w-[85vw] shadow-xl">
              <button
                type="button"
                onClick={() => {
                  setMoNganKeo(false);
                }}
                aria-label="Đóng menu"
                className="absolute top-3 right-2 z-10 inline-flex size-10 items-center justify-center rounded-o text-menu-chu hover:bg-white/10"
              >
                <X size={20} aria-hidden="true" />
              </button>
              <Sidebar
                me={me}
                onDangXuat={xuLyDangXuat}
                dangDangXuat={dangXuat.isPending}
                onChonMuc={() => {
                  setMoNganKeo(false);
                }}
              />
            </div>
          </div>
        )}

        <div className="lg:flex">
          {/* `self-start`: hàng này là flex, mặc định mọi ô con **giãn bằng ô cao
              nhất**. Không có nó thì trên trang dài, thanh bên nhận chiều cao
              của cả tài liệu và `position: sticky` mất tác dụng — thanh bên
              cuộn đi mất thay vì dính. */}
          <aside className="cot-ben hidden lg:sticky lg:top-(--cao-dai) lg:block lg:w-(--rong-thanh-ben) lg:shrink-0 lg:self-start">
            <Sidebar
              me={me}
              onDangXuat={xuLyDangXuat}
              dangDangXuat={dangXuat.isPending}
              gon={menuGon}
            />
          </aside>

          <div className="min-w-0 flex-1">
            <ThanhTren
              me={me}
              onMoNganKeo={() => {
                setMoNganKeo(true);
              }}
              menuGon={menuGon}
              onDoiMenuGon={() => {
                setMenuGon((cu) => !cu);
              }}
            />

            {/*
              `@container/noi-dung`: vùng nội dung là **khung đo** của mọi khối
              bên trong.

              Breakpoint theo khung nhìn (`lg:`, `xl:`) trả lời sai câu hỏi mà
              các khối này cần: chúng không quan tâm màn hình rộng bao nhiêu,
              chúng quan tâm **chỗ thật sự còn lại** sau khi trừ thanh bên.
              Trên laptop 1280px, `lg:` và `xl:` đều đã bật trong khi vùng nội
              dung chỉ 992px — đó là lý do một dải sáu ô chỉ số ra sáu cột rộng
              130px. Xem `docs/ui.md` mục 9.
            */}
            <main className="@container/noi-dung mx-auto w-full max-w-(--rong-noi-dung) px-4 py-5 md:px-7 md:py-6">
              {children}
            </main>
          </div>
        </div>
      </div>
    </KyChungProvider>
  );
}
